import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Bold, Italic, List, ListOrdered, Link as LinkIcon, Code2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Variable picker options mapped to Lead Form fields
// Note: This should match the VARIABLE_OPTIONS in TemplatesPage.js
const VARIABLE_OPTIONS = [
    {
        category: 'Lead Info',
        variables: [
            { label: 'First Name (Virtual)', value: 'first_name' },
            { label: 'Full Name', value: 'full_name' },
            { label: 'Email', value: 'email' },
            { label: 'Company', value: 'company' },
            { label: 'Job Title', value: 'job_title' },
            { label: 'Website', value: 'website' },
            { label: 'Phone Number', value: 'phone_number' },
            { label: 'LinkedIn Profile', value: 'linkedin_profile' },
            { label: 'Estimated Value', value: 'estimated_value' },
            { label: 'Source', value: 'source' },
        ]
    },
    {
        category: 'Sender Info',
        variables: [
            { label: 'My Name', value: 'sender_name' },
            { label: 'My Email', value: 'sender_email' },
        ]
    }
];

const RichTextEditor = ({
    content,
    onChange,
    placeholder = 'Write your message...',
    leadId = null,
    accountId = null,
    onMissingFields = null,
    onSubjectChange = null,
    onEditorReady = null,
    showVariablePicker = true
}) => {
    const [variablePickerOpen, setVariablePickerOpen] = useState(false);
    const [variableAutocompleteOpen, setVariableAutocompleteOpen] = useState(false);
    const [variableQuery, setVariableQuery] = useState('');
    const [variablePosition, setVariablePosition] = useState({ top: 0, left: 0 });
    const [slashCommandOpen, setSlashCommandOpen] = useState(false);
    const [slashQuery, setSlashQuery] = useState('');
    const [templates, setTemplates] = useState([]);
    const [slashPosition, setSlashPosition] = useState({ top: 0, left: 0 });
    const slashCommandRef = useRef(null);

    // Refs to track current values for handleKeyDown callback (fixes stale closure)
    const variableAutocompleteOpenRef = useRef(variableAutocompleteOpen);
    const slashCommandOpenRef = useRef(slashCommandOpen);
    const filteredVariablesRef = useRef([]);
    const filteredTemplatesRef = useRef([]);
    const insertVariableRef = useRef(null);
    const insertTemplateRef = useRef(null);
    const setVariableAutocompleteOpenRef = useRef(setVariableAutocompleteOpen);
    const setSlashCommandOpenRef = useRef(setSlashCommandOpen);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // Exclude Link from StarterKit since we're adding it separately
                link: false,
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-blue-600 underline',
                },
            }),
        ],
        content: content || '',
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onChange(html);

            // Check for slash command
            checkSlashCommand(editor);

            // Check for variable autocomplete (typing {)
            checkVariableAutocomplete(editor);
        },
        onCreate: ({ editor }) => {
            // Expose editor instance to parent component immediately
            // The editor is ready when onCreate is called
            if (onEditorReady) {
                // Call immediately - editor is ready at this point
                onEditorReady(editor);
            }
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-4 text-slate-700',
            },
            handleKeyDown: (view, event) => {
                // Handle Enter key in variable autocomplete (using refs to avoid stale closure)
                if (variableAutocompleteOpenRef.current && event.key === 'Enter') {
                    event.preventDefault();
                    if (filteredVariablesRef.current.length > 0) {
                        insertVariableRef.current(filteredVariablesRef.current[0].value);
                    }
                    return true;
                }
                // Handle Enter key in slash command menu (using refs to avoid stale closure)
                if (slashCommandOpenRef.current && event.key === 'Enter') {
                    event.preventDefault();
                    if (filteredTemplatesRef.current.length > 0) {
                        insertTemplateRef.current(filteredTemplatesRef.current[0].id);
                    }
                    return true;
                }
                // Handle Escape to close autocomplete or slash command (using refs to avoid stale closure)
                if ((variableAutocompleteOpenRef.current || slashCommandOpenRef.current) && event.key === 'Escape') {
                    setVariableAutocompleteOpenRef.current(false);
                    setSlashCommandOpenRef.current(false);
                    return true;
                }
                // Check for slash command or variable autocomplete on keypress
                if (event.key === '/' || event.key === '{' || event.key.length === 1) {
                    setTimeout(() => {
                        if (editor) {
                            checkSlashCommand(editor);
                            checkVariableAutocomplete(editor);
                        }
                    }, 0);
                }
                return false;
            },
        },
    });

    // Fetch templates for slash command
    useEffect(() => {
        if (slashCommandOpen) {
            fetchTemplates();
        }
    }, [slashCommandOpen]);

    const fetchTemplates = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/templates`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTemplates(response.data);
        } catch (error) {
            console.error('Failed to fetch templates:', error);
        }
    };

    const checkSlashCommand = useCallback((editor) => {
        if (!editor) return;

        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;

        // Get text from start of line to cursor
        const lineStart = $from.start($from.depth);
        const textBeforeCursor = state.doc.textBetween(lineStart, $from.pos, '\n');

        // Check if we're typing a slash command
        const slashMatch = textBeforeCursor.match(/\/(\w*)$/);

        if (slashMatch) {
            const query = slashMatch[1].toLowerCase();
            setSlashQuery(query);

            // Get cursor position for popover - coordsAtPos returns viewport coordinates
            const coords = editor.view.coordsAtPos($from.pos);

            // Use viewport coordinates directly for fixed positioning in portal
            setSlashPosition({
                top: coords.top,
                left: coords.left
            });

            setSlashCommandOpen(true);
        } else {
            setSlashCommandOpen(false);
        }
    }, []);

    const checkVariableAutocomplete = useCallback((editor) => {
        if (!editor) return;

        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;

        // Get text from start of line to cursor
        const lineStart = $from.start($from.depth);
        const textBeforeCursor = state.doc.textBetween(lineStart, $from.pos, '\n');

        // Check if we're typing { for variable autocomplete
        const variableMatch = textBeforeCursor.match(/\{(\w*)$/);

        if (variableMatch) {
            const query = variableMatch[1].toLowerCase();
            setVariableQuery(query);

            // Get cursor position for popover - coordsAtPos returns viewport coordinates
            const coords = editor.view.coordsAtPos($from.pos);

            // Use viewport coordinates directly for fixed positioning in portal
            setVariablePosition({
                top: coords.top,
                left: coords.left
            });

            setVariableAutocompleteOpen(true);
        } else {
            setVariableAutocompleteOpen(false);
        }
    }, []);

    const insertVariable = (variable) => {
        if (!editor) return;

        const varText = `{{${variable}}}`;

        // If autocomplete is open, we need to remove the { and query text first
        if (variableAutocompleteOpen) {
            const { state } = editor;
            const { selection } = state;
            const { $from } = selection;

            // Find the { character position
            const lineStart = $from.start($from.depth);
            const textBeforeCursor = state.doc.textBetween(lineStart, $from.pos, '\n');
            const variableMatch = textBeforeCursor.match(/\{(\w*)$/);

            if (variableMatch) {
                const matchLength = variableMatch[0].length; // Length of {query
                const deleteFrom = $from.pos - matchLength;

                // Delete the {query text and insert the full variable
                editor.chain()
                    .focus()
                    .deleteRange({ from: deleteFrom, to: $from.pos })
                    .insertContent(varText)
                    .run();
            } else {
                // Fallback: just insert
                editor.chain().focus().insertContent(varText).run();
            }
        } else {
            // Normal insertion
            editor.chain().focus().insertContent(varText).run();
        }

        setVariablePickerOpen(false);
        setVariableAutocompleteOpen(false);
    };

    const insertTemplate = async (templateId) => {
        if (!editor) return;

        try {
            const token = localStorage.getItem('token');
            const params = { lead_id: leadId };
            if (accountId) {
                params.account_id = accountId;
            }
            const response = await axios.post(
                `${API_URL}/templates/${templateId}/process`,
                null,
                {
                    params: params,
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const { subject, body_html, missing_fields } = response.data;

            // Remove the slash command text
            const { state } = editor;
            const { selection } = state;
            const { $from } = selection;
            const lineStart = $from.start($from.depth);
            const textBeforeCursor = state.doc.textBetween(lineStart, $from.pos, '\n');
            const slashMatch = textBeforeCursor.match(/\/(\w*)$/);

            if (slashMatch) {
                const deleteLength = slashMatch[0].length;
                editor.chain()
                    .focus()
                    .deleteRange({ from: $from.pos - deleteLength, to: $from.pos })
                    .insertContent(body_html)
                    .run();
            }

            // Auto-fill subject if empty
            if (onSubjectChange && subject) {
                onSubjectChange(subject);
            }

            // Handle missing fields
            if (missing_fields && missing_fields.length > 0 && onMissingFields) {
                onMissingFields(missing_fields);
            }

            setSlashCommandOpen(false);
        } catch (error) {
            console.error('Failed to process template:', error);
        }
    };

    const getAllVariables = () => {
        return VARIABLE_OPTIONS.flatMap(group => group.variables);
    };

    // If query is empty (just typed {), show all variables
    // Otherwise filter by query
    const filteredVariables = variableQuery === ''
        ? getAllVariables()
        : getAllVariables().filter(v =>
            v.value.toLowerCase().includes(variableQuery.toLowerCase()) ||
            v.label.toLowerCase().includes(variableQuery.toLowerCase())
        );

    const filteredTemplates = templates.filter(t =>
        t.name.toLowerCase().includes(slashQuery.toLowerCase())
    );

    // Update refs whenever values change (fixes stale closure in handleKeyDown)
    useEffect(() => {
        variableAutocompleteOpenRef.current = variableAutocompleteOpen;
        slashCommandOpenRef.current = slashCommandOpen;
        filteredVariablesRef.current = filteredVariables;
        filteredTemplatesRef.current = filteredTemplates;
        insertVariableRef.current = insertVariable;
        insertTemplateRef.current = insertTemplate;
        setVariableAutocompleteOpenRef.current = setVariableAutocompleteOpen;
        setSlashCommandOpenRef.current = setSlashCommandOpen;
    }, [variableAutocompleteOpen, slashCommandOpen, filteredVariables, filteredTemplates, insertVariable, insertTemplate]);

    const setLink = React.useCallback(() => {
        if (!editor) return;

        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);

        // cancelled
        if (url === null) {
            return;
        }

        // empty
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }

        // update link
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    if (!editor) {
        return null;
    }

    return (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white relative">
            <div className="flex items-center gap-1 p-2 border-b border-slate-200 bg-slate-50">
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={`h-8 w-8 p-0 ${editor.isActive('bold') ? 'bg-slate-200' : ''}`}
                    title="Bold"
                >
                    <Bold className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={`h-8 w-8 p-0 ${editor.isActive('italic') ? 'bg-slate-200' : ''}`}
                    title="Italic"
                >
                    <Italic className="h-4 w-4" />
                </Button>
                <div className="w-px h-6 bg-slate-300 mx-1" />
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={`h-8 w-8 p-0 ${editor.isActive('bulletList') ? 'bg-slate-200' : ''}`}
                    title="Bullet List"
                >
                    <List className="h-4 w-4" />
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={`h-8 w-8 p-0 ${editor.isActive('orderedList') ? 'bg-slate-200' : ''}`}
                    title="Numbered List"
                >
                    <ListOrdered className="h-4 w-4" />
                </Button>
                <div className="w-px h-6 bg-slate-300 mx-1" />
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={setLink}
                    className={`h-8 w-8 p-0 ${editor.isActive('link') ? 'bg-slate-200' : ''}`}
                    title="Add Link"
                >
                    <LinkIcon className="h-4 w-4" />
                </Button>
                {showVariablePicker && (
                    <>
                        <div className="w-px h-6 bg-slate-300 mx-1" />
                        <Popover open={variablePickerOpen} onOpenChange={setVariablePickerOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className={`h-8 w-8 p-0 ${variablePickerOpen ? 'bg-slate-200' : ''}`}
                                    title="Insert Variable"
                                >
                                    <Code2 className="h-4 w-4" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 p-0" align="start">
                                <Command>
                                    <CommandInput placeholder="Search variables..." />
                                    <CommandList>
                                        <CommandEmpty>No variables found.</CommandEmpty>
                                        {VARIABLE_OPTIONS.map((group) => (
                                            <CommandGroup key={group.category} heading={group.category}>
                                                {group.variables.map((variable) => (
                                                    <CommandItem
                                                        key={variable.value}
                                                        onSelect={() => insertVariable(variable.value)}
                                                        className="cursor-pointer"
                                                    >
                                                        <div className="flex flex-col">
                                                            <span className="font-medium">{variable.label}</span>
                                                            <span className="text-xs text-slate-500">{`{{${variable.value}}}`}</span>
                                                        </div>
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        ))}
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </>
                )}
            </div>
            <EditorContent editor={editor} />

            {/* Variable Autocomplete Popover - Using Portal to render outside modal */}
            {variableAutocompleteOpen && typeof document !== 'undefined' && createPortal(
                <div
                    className="fixed z-[100] w-80 rounded-md border bg-white shadow-lg"
                    style={{
                        top: `${variablePosition.top + 20}px`,
                        left: `${variablePosition.left}px`,
                    }}
                >
                    <Command>
                        <CommandInput placeholder="Search variables..." value={variableQuery} />
                        <CommandList>
                            <CommandEmpty>No variables found.</CommandEmpty>
                            {VARIABLE_OPTIONS.map((group) => {
                                // Filter variables for this group
                                const groupVariables = filteredVariables.filter(v =>
                                    group.variables.some(gv => gv.value === v.value)
                                );

                                // Only show group if it has matching variables
                                if (groupVariables.length === 0) return null;

                                return (
                                    <CommandGroup key={group.category} heading={group.category}>
                                        {groupVariables.map((variable) => (
                                            <CommandItem
                                                key={variable.value}
                                                onSelect={() => insertVariable(variable.value)}
                                                className="cursor-pointer"
                                            >
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{variable.label}</span>
                                                    <span className="text-xs text-slate-500 font-mono">{`{{${variable.value}}}`}</span>
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                );
                            })}
                        </CommandList>
                    </Command>
                </div>,
                document.body
            )}

            {/* Slash Command Popover - Using Portal to render outside modal */}
            {slashCommandOpen && typeof document !== 'undefined' && createPortal(
                <div
                    ref={slashCommandRef}
                    className="fixed z-[100] w-80 rounded-md border bg-white shadow-lg"
                    style={{
                        top: `${slashPosition.top}px`,
                        left: `${slashPosition.left}px`,
                        position: 'fixed',
                    }}
                >
                    <Command>
                        <CommandInput placeholder="Search templates..." value={slashQuery} />
                        <CommandList>
                            <CommandEmpty>No templates found.</CommandEmpty>
                            <CommandGroup heading="Templates">
                                {filteredTemplates.map((template) => (
                                    <CommandItem
                                        key={template.id}
                                        onSelect={() => insertTemplate(template.id)}
                                        className="cursor-pointer"
                                    >
                                        <Sparkles className="h-4 w-4 mr-2" />
                                        <span>{template.name}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </div>,
                document.body
            )}

            {/* Variable Chip Styling */}
            <style>{`
                .prose :global([data-variable]) {
                    display: inline-block;
                    background-color: #f1f5f9;
                    color: #475569;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-family: 'Courier New', monospace;
                    font-size: 0.9em;
                    border: 1px solid #cbd5e1;
                }
                .prose :global([data-variable-missing]) {
                    background-color: #fee2e2;
                    color: #dc2626;
                    border-color: #fca5a5;
                }
            `}</style>
        </div>
    );
};

export default RichTextEditor;
